/**
 * Background Music Service
 * Enterprise-grade service for managing TTS background music configurations.
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import api from '../helpers/axios';
import type { 
  BackgroundMusic, 
  BackgroundMusicPayload, 
  BGMSearchOptions,
  BGMCategory,
  BGMMood,
  BGMTempo
} from '../types/aiTypes';

// API base path for BGM endpoints
const API_BASE = '/api/ai/bgm';

/**
 * Response wrapper for list operations.
 */
interface BGMListResponse {
  success: boolean;
  count: number;
  bgms: BackgroundMusic[];
  message?: string;
}

/**
 * Response wrapper for single BGM operations.
 */
interface BGMSingleResponse {
  success: boolean;
  bgm: BackgroundMusic;
  message?: string;
}

/**
 * BGM options for dropdowns.
 */
interface BGMOptionsResponse {
  success: boolean;
  categories: BGMCategory[];
  moods: BGMMood[];
  tempos: BGMTempo[];
}

/**
 * Background Music Service
 * Provides CRUD operations and search functionality for background music.
 */
class BackgroundMusicService {
  /**
   * Get all active background music.
   */
  async getAllActiveBgm(): Promise<BackgroundMusic[]> {
    try {
      const response = await api.get<BackgroundMusic[] | BGMListResponse>(API_BASE);
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data.bgms || []);
    } catch (error) {
      console.error('Error fetching active BGM:', error);
      throw error;
    }
  }

  /**
   * Get all background music including inactive.
   */
  async getAllBgm(): Promise<BackgroundMusic[]> {
    try {
      const response = await api.get<BackgroundMusic[] | BGMListResponse>(`${API_BASE}/all`);
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data.bgms || []);
    } catch (error) {
      console.error('Error fetching all BGM:', error);
      throw error;
    }
  }

  /**
   * Get background music by ID.
   */
  async getBgmById(bgmId: number): Promise<BackgroundMusic | null> {
    try {
      const response = await api.get<BackgroundMusic | BGMSingleResponse>(`${API_BASE}/${bgmId}`);
      // Handle both direct object response and wrapped response
      if ('bgmId' in response.data) {
        return response.data as BackgroundMusic;
      }
      return (response.data as BGMSingleResponse).bgm;
    } catch (error) {
      console.error(`Error fetching BGM ${bgmId}:`, error);
      throw error;
    }
  }

  /**
   * Get BGM by category.
   */
  async getBgmByCategory(category: BGMCategory): Promise<BackgroundMusic[]> {
    try {
      const response = await api.get<BackgroundMusic[] | BGMListResponse>(`${API_BASE}/category/${category}`);
      return Array.isArray(response.data) ? response.data : (response.data.bgms || []);
    } catch (error) {
      console.error(`Error fetching BGM for category ${category}:`, error);
      throw error;
    }
  }

  /**
   * Get BGM by mood.
   */
  async getBgmByMood(mood: BGMMood): Promise<BackgroundMusic[]> {
    try {
      const response = await api.get<BackgroundMusic[] | BGMListResponse>(`${API_BASE}/mood/${mood}`);
      return Array.isArray(response.data) ? response.data : (response.data.bgms || []);
    } catch (error) {
      console.error(`Error fetching BGM for mood ${mood}:`, error);
      throw error;
    }
  }

  /**
   * Create a new background music configuration.
   */
  async createBgm(payload: BackgroundMusicPayload): Promise<BackgroundMusic> {
    try {
      const response = await api.post<BackgroundMusic | BGMSingleResponse>(API_BASE, payload);
      // Handle both direct object response and wrapped response
      if ('bgmId' in response.data) {
        return response.data as BackgroundMusic;
      }
      return (response.data as BGMSingleResponse).bgm;
    } catch (error) {
      console.error('Error creating BGM:', error);
      throw error;
    }
  }

  /**
   * Update an existing background music configuration.
   */
  async updateBgm(bgmId: number, payload: BackgroundMusicPayload): Promise<BackgroundMusic> {
    try {
      const response = await api.put<BackgroundMusic | BGMSingleResponse>(`${API_BASE}/${bgmId}`, payload);
      // Handle both direct object response and wrapped response
      if ('bgmId' in response.data) {
        return response.data as BackgroundMusic;
      }
      return (response.data as BGMSingleResponse).bgm;
    } catch (error) {
      console.error(`Error updating BGM ${bgmId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a background music configuration.
   */
  async deleteBgm(bgmId: number): Promise<void> {
    try {
      await api.delete(`${API_BASE}/${bgmId}`);
    } catch (error) {
      console.error(`Error deleting BGM ${bgmId}:`, error);
      throw error;
    }
  }

  /**
   * Set a BGM as the default for its category.
   */
  async setAsDefault(bgmId: number): Promise<BackgroundMusic> {
    try {
      const response = await api.post<BackgroundMusic | BGMSingleResponse>(`${API_BASE}/${bgmId}/set-default`);
      // Handle both direct object response and wrapped response
      if ('bgmId' in response.data) {
        return response.data as BackgroundMusic;
      }
      return (response.data as BGMSingleResponse).bgm;
    } catch (error) {
      console.error(`Error setting BGM ${bgmId} as default:`, error);
      throw error;
    }
  }

  /**
   * Get BGM options for dropdown selectors.
   */
  async getBgmOptions(): Promise<BGMOptionsResponse> {
    try {
      const response = await api.get<BGMOptionsResponse>(`${API_BASE}/options`);
      return response.data;
    } catch (error) {
      console.error('Error fetching BGM options:', error);
      throw error;
    }
  }

  /**
   * Search BGM with filters.
   */
  async searchBgm(options: BGMSearchOptions): Promise<BackgroundMusic[]> {
    try {
      const params = new URLSearchParams();
      
      if (options.categories?.length) {
        params.set('categories', options.categories.join(','));
      }
      if (options.moods?.length) {
        params.set('moods', options.moods.join(','));
      }
      if (options.tempos?.length) {
        params.set('tempos', options.tempos.join(','));
      }
      if (options.isActive !== undefined) {
        params.set('isActive', String(options.isActive));
      }

      const response = await api.get<BackgroundMusic[] | BGMListResponse>(
        `${API_BASE}/search?${params.toString()}`
      );
      return Array.isArray(response.data) ? response.data : (response.data.bgms || []);
    } catch (error) {
      console.error('Error searching BGM:', error);
      throw error;
    }
  }

  /**
   * Upload audio file for BGM (Step 1 of 2-step pattern).
   * Following the enterprise pattern used by article/video uploads.
   * @returns Object containing fileUrl to be used when creating BGM
   */
  async uploadAudioFile(file: File): Promise<{ fileUrl: string; fileName: string; contentType: string; size: number }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<{
        fileUrl: string;
        fileName: string;
        contentType: string;
        size: number;
        message: string;
      }>(`${API_BASE}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        fileUrl: response.data.fileUrl,
        fileName: response.data.fileName,
        contentType: response.data.contentType,
        size: response.data.size,
      };
    } catch (error) {
      console.error('Error uploading audio file:', error);
      throw error;
    }
  }

  /**
   * Upload a new BGM file with metadata (2-step pattern).
   * Step 1: Upload file
   * Step 2: Create BGM record with file URL
   */
  async uploadBgmFile(file: File, metadata: Partial<BackgroundMusicPayload>): Promise<BackgroundMusic> {
    try {
      // Step 1: Upload the audio file
      const uploadResult = await this.uploadAudioFile(file);

      // Step 2: Create BGM record with the file URL
      const bgmPayload: BackgroundMusicPayload = {
        ...metadata,
        fileUrl: uploadResult.fileUrl,
        originalFilename: uploadResult.fileName,
        fileSizeBytes: uploadResult.size,
        audioFormat: file.name.split('.').pop()?.toLowerCase() || 'mp3',
      } as BackgroundMusicPayload;

      return await this.createBgm(bgmPayload);
    } catch (error) {
      console.error('Error uploading BGM file:', error);
      throw error;
    }
  }

  /**
   * Update a BGM with a new audio file (2-step pattern).
   * Step 1: Upload new file
   * Step 2: Update BGM record with new file URL
   */
  async updateBgmWithFile(bgmId: number, file: File, metadata: Partial<BackgroundMusicPayload>): Promise<BackgroundMusic> {
    try {
      // Step 1: Upload the new audio file to update the BGM's file
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await api.post<{
        fileUrl: string;
        bgmId: number;
        fileName: string;
        message: string;
      }>(`${API_BASE}/${bgmId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Step 2: If metadata needs to be updated, update the BGM record
      if (Object.keys(metadata).length > 0) {
        const updatePayload: BackgroundMusicPayload = {
          ...metadata,
          fileUrl: uploadResponse.data.fileUrl,
          originalFilename: uploadResponse.data.fileName,
          audioFormat: file.name.split('.').pop()?.toLowerCase() || 'mp3',
        } as BackgroundMusicPayload;

        return await this.updateBgm(bgmId, updatePayload);
      }

      // If no metadata, fetch and return the updated BGM
      const bgm = await this.getBgmById(bgmId);
      if (!bgm) {
        throw new Error('BGM not found after update');
      }
      return bgm;
    } catch (error) {
      console.error(`Error updating BGM ${bgmId} with file:`, error);
      throw error;
    }
  }

  /**
   * Preview/play a BGM file.
   */
  async getBgmPreviewUrl(bgmId: number): Promise<string> {
    try {
      const bgm = await this.getBgmById(bgmId);
      return bgm?.fileUrl || '';
    } catch (error) {
      console.error(`Error getting BGM preview URL for ${bgmId}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const backgroundMusicService = new BackgroundMusicService();
export default backgroundMusicService;
